import { useState } from "react";
import { useSelector } from "react-redux";
import { selectStopCompletedDate } from "../../../ducks/data/route-with-stops/selectors";
import { useTranslation } from "react-i18next";
import { stopCompleted as stopCompletedActivity } from "../../../ducks/app/driver-activities";
import { DateTime } from "luxon";
import ActivityTimeEditDialog from "./ActivityTimeEditDialog";
import EditIcon from "@mui/icons-material/Edit";
import { useActions } from "../../../hooks/useActions";
import { PayloadAction } from "@reduxjs/toolkit";
import { StopNew } from "../../../ducks/data/route-with-stops/types";
import LoadingButton from "../../../components/LoadingButton";
import { useInFlightGuard } from "../../../hooks/useInFlightGuard";
import { useParams } from "../hooks";
import { parseTrailerIdParam } from "../../../routes";

type StopCompletedProps = {
  date: DateTime | null;
  stop: StopNew;
  buttonText: string;
  editTitle: string;
  action: (input: {
    stopId: string;
    cargoId: string;
    time?: DateTime;
  }) => Promise<PayloadAction<any>>;
};

const StopCompleted = (props: StopCompletedProps) => {
  const { stop } = props;
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(false);
  const guard = useInFlightGuard();

  const date = props.date;

  const onDateSelected = (date: DateTime) => {
    guard(() => {
      setActionInProgress(true);
      return props
        .action({
          stopId: stop.id,
          cargoId: stop.cargoId,
          time: date!,
        })
        .then(() => setShowEditDialog(false))
        .finally(() => setActionInProgress(false));
    });
  };

  const onButtonClick = () => {
    if (date) {
      setShowEditDialog(true);
    } else {
      guard(() => {
        setActionInProgress(true);
        return props
          .action({ stopId: stop.id, cargoId: stop.cargoId })
          .finally(() => setActionInProgress(false));
      });
    }
  };

  return (
    <>
      {showEditDialog && (
        <ActivityTimeEditDialog
          actionInProgress={actionInProgress}
          initialValue={props.date!}
          title={props.editTitle}
          onClose={() => setShowEditDialog(false)}
          onDateSelected={onDateSelected}
        />
      )}
      <LoadingButton
        loading={actionInProgress}
        fullWidth
        size="large"
        variant="contained"
        onClick={onButtonClick}
        startIcon={date ? <EditIcon /> : null}
      >
        <span>{props.buttonText}</span>
      </LoadingButton>
    </>
  );
};

export const PickupDropoffButton = ({ stop }: { stop: StopNew }) => {
  const { t } = useTranslation(["translation", "stopPage"]);
  const date = useSelector(selectStopCompletedDate(stop.id));
  const params = useParams();
  const truckId = (params.truckId as string) || "";
  const routeId = (params.routeId as string) || "";
  const unpoweredTrailerId = parseTrailerIdParam(params.trailerId as string);
  const actions = useActions({ stopCompletedActivity });
  const isPickup = stop.type === "PICKUP";
  const buttonText = isPickup
    ? date
      ? `${t("Collected")}  ${date?.toFormat("HH:mm")}`
      : t("Collect")
    : date
      ? `${t("Delivered")}  ${date?.toFormat("HH:mm")}`
      : t("Deliver");

  return (
    <StopCompleted
      stop={stop}
      date={date}
      buttonText={buttonText}
      action={(input: { stopId: string; cargoId: string; time?: DateTime }) =>
        actions.stopCompletedActivity({
          routeId,
          cargoId: input.cargoId,
          stopId: input.stopId,
          dateTime: input.time || null,
          truckId,
          unpoweredTrailerId,
        })
      }
      editTitle={
        isPickup
          ? t("stopPage:stopCompleted.editPickup")
          : t("stopPage:stopCompleted.editDropoff")
      }
    />
  );
};
